import React, { useRef, useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { IEmojiPickerProps, IEmojiData } from 'emoji-picker-react';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import CancelButton from 'components/CancelButton';
import { Row, Col } from 'components/layout';
import { Input } from 'components/form';
import Icon from 'components/Icon';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { REGEX } from 'lib/utils/regex';
import styles from './TitleValueEmojiV2.module.scss';

const EmojiPickerNoSSRWrapper = dynamic<IEmojiPickerProps>(
  () => import('emoji-picker-react'),
  {
    ssr: false,
  }
);

type AddTitleValueEmojiProps = {
  isCustomLink?: boolean | undefined;
  onRemove: () => void;
  index: number;
  name: string;
  isHighlight?: boolean;
  onUpdate: (
    index: number,
    data: {
      title?: string;
      name?: string;
      link?: string;
      value?: string;
      emoji?: string;
    }
  ) => void;
  isStat?: boolean;
  showControls?: boolean;
};

const AddTitleValueEmoji: React.FC<AddTitleValueEmojiProps> = ({
  isCustomLink,
  onRemove,
  index,
  name,
  isHighlight,
  onUpdate,
  isStat,
  showControls = true,
}) => {
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const emojiKey = `${name}.${index}.emoji`;
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const sectionErrors = errors?.[name];

  const watchFieldArray = watch(name);
  const emoji = watchFieldArray[index]?.emoji;

  useEffect(() => {
    if (!isHighlight) {
      register(emojiKey);
    }
  }, [register, isHighlight]);

  const onEmojiSelect = (
    event: React.MouseEvent<Element, MouseEvent>,
    emojiObject: IEmojiData
  ) => {
    onUpdate(index, { ...watchFieldArray[index], emoji: emojiObject?.emoji });
    setEmojiPickerOpen(false);
  };
  const titleFieldKey = !isHighlight ? 'title' : 'name';

  const subTitleKey = isCustomLink ? 'link' : 'value';

  useOnClickOutside({
    ref: pickerRef,
    onOutsideClick: () => setEmojiPickerOpen(false),
  });

  const titleInputError = sectionErrors?.[index]?.[titleFieldKey];
  const subTitleInputError = sectionErrors?.[index]?.[subTitleKey];
  const emojiInputError = sectionErrors?.[index]?.['emoji'];

  return (
    <div className={styles.form}>
      <Row className={styles.formRow} align="center" wrap={false}>
        {!isHighlight ? (
          <Col className={styles.emoji}>
            {emojiPickerOpen ? (
              <div ref={pickerRef} className={styles.emojiContainer}>
                <EmojiPickerNoSSRWrapper
                  onEmojiClick={onEmojiSelect}
                  disableAutoFocus={true}
                  groupNames={{ smileys_people: 'PEOPLE' }}
                  native
                />
              </div>
            ) : (
              <button
                type="button"
                className={cn(styles.emojiButton, {
                  [styles.disabled]: !emoji,
                  [styles.emojiError]: emojiInputError,
                })}
                onClick={() => setEmojiPickerOpen(true)}
              >
                {emoji ? (
                  emoji
                ) : (
                  <Icon
                    iconName="icon_add-emoji"
                    size="auto"
                    className={styles.buttonIcon}
                  />
                )}
              </button>
            )}
          </Col>
        ) : null}
        <Col span={isHighlight ? 24 : 11}>
          <Input
            placeholder={isHighlight ? 'Name' : 'Title'}
            inputClassName={styles.inputField}
            {...register(`${name}.${index}.${titleFieldKey}`, {
              required: 'Required',
            })}
            isValid={!titleInputError}
            helperText={titleInputError ? titleInputError.message : ''}
            maxLength={
              isCustomLink ? 60 : isStat ? 44 : isHighlight ? 80 : undefined
            }
          />
        </Col>
        {!isHighlight ? (
          <Col span={11}>
            <Input
              placeholder={isCustomLink ? 'URL' : 'Value'}
              {...register(`${name}.${index}.${subTitleKey}`, {
                required: 'Required',
                ...(isCustomLink
                  ? {
                      pattern: {
                        value: REGEX.URL,
                        message: 'Enter a valid url',
                      },
                    }
                  : {}),
              })}
              inputClassName={styles.inputField}
              isValid={!subTitleInputError}
              helperText={subTitleInputError ? subTitleInputError.message : ''}
              maxLength={isStat ? 36 : undefined}
            />
          </Col>
        ) : null}
      </Row>
      <CancelButton
        onRemove={onRemove}
        className={styles.cancelButton}
        isShow={showControls}
      />
    </div>
  );
};

export { AddTitleValueEmoji };
